/**
 * SOLID Review
 * S — Strong. One concern: what the client's error reporting keeps, takes and plans to send.
 * O — Strong. A new field of a report is one more key here.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. The ledger, the command, the plan — each its own schema.
 * D — Strong. zod only.
 */
import { z } from 'zod';

/**
 * CLIENT ERROR REPORTING (plan 2026-10-09, EP5). Where a browser caught an error. Mirrors the wire
 * contract's `ClientErrorKind` — the Domain may not import the contract, which Infrastructure applies.
 */
const ClientErrorKind = z.enum(['UNCAUGHT', 'REJECTION', 'RENDER', 'PORT', 'BOOT', 'THIRD_PARTY']);

/** At most this many reports leave one page load. */
export const CLIENT_ERROR_SEND_BUDGET = 20;
/** One fingerprint is sent at most once per this window. */
export const CLIENT_ERROR_REPEAT_MS = 60_000;
/** How many fingerprints the ledger remembers; the oldest is forgotten past it. */
export const CLIENT_ERROR_LEDGER_SIZE = 50;

/**
 * WHAT THIS PAGE LOAD HAS SENT — never what it merely saw. A write happens only when a report goes out,
 * so an error thrown on every animation frame costs one store write a minute, not sixty a second.
 */
export const ClientDiagnostics = z.object({
  sends: z.number().int().min(0),
  /** Fingerprint → when it was last sent (ms), oldest first. */
  lastSent: z.array(z.tuple([z.string(), z.number()])).max(CLIENT_ERROR_LEDGER_SIZE),
}).meta({ id: 'ClientDiagnostics' });

/** Nothing sent yet. */
export const NO_DIAGNOSTICS: z.infer<typeof ClientDiagnostics> = { sends: 0, lastSent: [] };

/** An error as the Framework caught it. `now` is the browser's clock (no LOCAL clock port — as FE5c's media). */
export const CommandClientError = z.object({
  kind: ClientErrorKind,
  message: z.string(),
  stack: z.string().nullable(),
  port: z.string().nullable(),
  op: z.string().nullable(),
  code: z.string().nullable(),
  now: z.number(),
}).meta({ id: 'CommandClientError' });

/** What to send — Infrastructure adds the page, the build and the scrub, and checks it against the wire. */
export const RequestClientError = z.object({
  kind: ClientErrorKind,
  message: z.string(),
  stack: z.string().nullable(),
  port: z.string().nullable(),
  op: z.string().nullable(),
  code: z.string().nullable(),
  count: z.number().int().min(1),
}).meta({ id: 'RequestClientError' });

/** The decision's answer: the report to send, or none — a repeat inside its window, or the budget spent. */
export const DtoClientErrorPlan = z.object({ report: RequestClientError.nullable() }).meta({ id: 'DtoClientErrorPlan' });

export type IEntityClientDiagnostics = z.infer<typeof ClientDiagnostics>;
export type ICommandClientError = z.infer<typeof CommandClientError>;
export type IPayloadClientError = z.infer<typeof RequestClientError>;
export type IEntityClientErrorPlan = z.infer<typeof DtoClientErrorPlan>;
